import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import { useSiteData, useLocaleText } from "../contexts/SiteDataContext";

const Gallery = () => {
  const { t } = useTranslation();
  const { gallery } = useSiteData();
  const { pick } = useLocaleText();
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Pagination configuration: 8 items per page (2 rows × 4 columns on desktop)
  const ITEMS_PER_PAGE = 8;

  const filters = [
    { key: "all", label: t("gallery.filters.all") },
    { key: "rental", label: t("gallery.filters.rental") },
    { key: "excavation", label: t("gallery.filters.excavation") },
    { key: "demolition", label: t("gallery.filters.demolition") },
    { key: "interlock", label: t("gallery.filters.interlock") },
    { key: "projects", label: t("gallery.filters.projects") },
  ];

  const filtered = filter === "all" ? gallery : gallery.filter((g) => g.cat === filter);
  
  // Reset to page 1 when filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filter]);
  
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedItems = filtered.slice(startIdx, startIdx + ITEMS_PER_PAGE);
  const showPagination = filtered.length > ITEMS_PER_PAGE;

  return (
    <div data-testid="page-gallery">
      <SEO title={t("nav.gallery")} description={t("gallery.subtitle")} path="/gallery" />

      <section className="relative bg-[#111111] text-white py-8 md:py-12 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/hero-gallery.jpg" alt="RTC project gallery" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#111111]/95 via-[#111111]/55 to-transparent rtl:bg-gradient-to-l" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-12 h-[2px] bg-[#FF5A00]" />
            <span className="font-mono-rtc text-[11px] uppercase tracking-[0.3em] text-[#FF5A00]">{t("gallery.eyebrow")}</span>
          </div>
          <h1 className="font-display uppercase font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">{t("gallery.title")}</h1>
          <p className="mt-6 max-w-xl text-white/80 text-lg">{t("gallery.subtitle")}</p>
        </div>
      </section>

      <section className="py-10 md:py-14 bg-[#FAFAFA] grid-bg">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-2 md:gap-3 mb-8 md:mb-10">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-4 md:px-5 py-2 md:py-2.5 font-bold uppercase tracking-wide text-[11px] md:text-xs border-2 transition-all duration-300 ${
                  filter === f.key
                    ? "bg-[#FF5A00] text-white border-[#FF5A00] shadow-[3px_3px_0_#111111]"
                    : "bg-white text-[#111111] border-[#111111] hover:border-[#FF5A00] hover:text-[#FF5A00] hover:shadow-[3px_3px_0_#FF5A00]"
                }`}
                data-testid={`gallery-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4" data-testid="gallery-grid">
            {paginatedItems.map((img, i) => (
              <button key={img.id || i} onClick={() => setLightbox(img)} className="group relative aspect-square overflow-hidden bg-gradient-to-br from-[#F8F8F8] to-[#EEEEEE] border-2 border-[#111111] hover:border-[#FF5A00] block-shadow-to-orange block transition-all duration-300" data-testid={`gallery-item-${i}`}>
                <img src={img.src} alt={pick(img.alt)} loading="lazy" className={`w-full h-full ${img.cat === "rental" ? "object-contain p-2" : "object-cover object-center"} group-hover:scale-105 transition-transform duration-500`} />
                <div className="absolute inset-0 bg-[#111111]/0 group-hover:bg-[#111111]/60 transition-colors flex items-end p-3 opacity-0 group-hover:opacity-100">
                  <span className="font-mono-rtc text-[10px] uppercase tracking-widest text-white">{img.cat} · {String(i + 1).padStart(2, "0")}</span>
                </div>
              </button>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20 font-mono-rtc text-sm uppercase tracking-widest text-[#52525B]">{t("common.noResults")}</div>
          )}

          {showPagination && (
            <div className="mt-10 md:mt-14 flex flex-col items-center justify-center gap-4">
              <div className="flex flex-wrap justify-center gap-2 items-center">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center font-bold text-xs border-2 border-[#111111] text-[#111111] hover:border-[#FF5A00] hover:text-[#FF5A00] transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[#111111] disabled:hover:text-[#111111]"
                  data-testid="gallery-pagination-prev"
                  title="Previous page"
                >
                  <ArrowLeft size={16} />
                </button>

                <div className="flex flex-wrap justify-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center font-bold text-xs border-2 transition-all duration-300 ${
                        page === currentPage
                          ? "bg-[#FF5A00] text-white border-[#FF5A00] shadow-[3px_3px_0_#111111]"
                          : "bg-white text-[#111111] border-[#111111] hover:border-[#FF5A00] hover:text-[#FF5A00] hover:shadow-[3px_3px_0_#FF5A00]"
                      }`}
                      data-testid={`gallery-page-${page}`}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center font-bold text-xs border-2 border-[#111111] text-[#111111] hover:border-[#FF5A00] hover:text-[#FF5A00] transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[#111111] disabled:hover:text-[#111111]"
                  data-testid="gallery-pagination-next"
                  title="Next page"
                >
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-[#111111]/95 flex items-center justify-center p-6" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <button className="absolute top-6 right-6 text-white border-2 border-white px-3 py-1 font-mono-rtc text-xs uppercase tracking-widest hover:bg-white hover:text-[#111111] transition-colors" onClick={() => setLightbox(null)} data-testid="gallery-lightbox-close">
            {t("common.close")} ✕
          </button>
          <img src={lightbox.src} alt={pick(lightbox.alt)} className="max-h-[85vh] max-w-[90vw] object-contain border-2 border-white" />
        </div>
      )}
    </div>
  );
};

export default Gallery;
